"use client";

import { Menu, ChevronRight, Home, MessageSquare } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, memo, useMemo } from "react";
import Image from "next/image";
import Link from "next/link";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import ActionButton from "../home/action-button";
import LoginButton from "../home/login-button";

interface MenuItem {
	title: string;
	url: string;
	description?: string;
	icon?: React.ReactNode;
	items?: MenuItem[];
	badge?: string;
	isNew?: boolean;
}

interface NavbarProps {
	logo?: {
		url: string;
		src: string;
		alt: string;
	};
	menu?: MenuItem[];
}

// Default icons mapping
const defaultIcons: Record<string, React.ReactNode> = {
	Home: <Home className="h-4 w-4" />,
	"Contact us": <MessageSquare className="h-4 w-4" />,
};

// Badge component for better reusability
const Badge = memo(({ text, type = "blue" }: { text: string; type?: "blue" | "purple" }) => {
	const bgColor = type === "blue" ? "bg-[#6CD2E4]/10 text-[#6CD2E4]" : "bg-[#7371FF]/10 text-[#7371FF]";
	return <span className={`ml-1 px-1.5 py-0.5 text-[10px] rounded-full ${bgColor} font-medium`}>{text}</span>;
});
Badge.displayName = "Badge";

// New Badge with animation
const NewBadge = memo(() => (
	<motion.span className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full bg-[#7371FF]/10 text-[#7371FF] font-medium" animate={{ scale: [1, 1.1, 1] }} transition={{ repeat: Infinity, duration: 2, repeatType: "loop" }}>
		New
	</motion.span>
));
NewBadge.displayName = "NewBadge";

// Desktop Menu Item Component
const DesktopMenuItem = memo(({ item, isActive }: { item: MenuItem; isActive: boolean }) => {
	if (item.items) {
		return (
			<div className={`text-gray-700 font-medium bg-transparent hover:bg-[#7371FF]/5 hover:text-[#7371FF] transition-all duration-300 ${isActive ? "text-[#7371FF]" : ""}`}>
				<div className="flex items-center gap-2">
					{item.icon || defaultIcons[item.title]}
					{item.title}
					{item.badge && <Badge text={item.badge} />}
					{item.isNew && <NewBadge />}
				</div>
			</div>
		);
	}

	return (
		<Link href={item.url} className={`group flex h-10 items-center justify-center rounded-lg px-4 py-2 text-sm font-medium hover:bg-[#7371FF]/5 hover:text-[#7371FF] transition-all duration-300 ${isActive ? "text-[#7371FF]" : "text-gray-700"}`}>
			<div className="flex items-center gap-2">
				{item.icon || defaultIcons[item.title]}
				{item.title}
				{item.badge && <Badge text={item.badge} />}
				{item.isNew && <NewBadge />}
			</div>
		</Link>
	);
});
DesktopMenuItem.displayName = "DesktopMenuItem";

// Mobile Menu Item Component
const MobileMenuItem = memo(({ item }: { item: MenuItem }) => {
	if (item.items) {
		return (
			<AccordionItem value={item.title} className="border-b border-[#7371FF]/10">
				<AccordionTrigger className="py-3 px-2 font-medium hover:no-underline hover:bg-[#7371FF]/5 rounded-lg transition-all">
					<div className="flex items-center gap-2 text-gray-800">
						<span className="p-1.5 rounded-lg bg-[#7371FF]/10 text-[#7371FF]">{item.icon || defaultIcons[item.title]}</span>
						{item.title}
						{item.badge && <Badge text={item.badge} />}
						{item.isNew && <NewBadge />}
					</div>
				</AccordionTrigger>
				<AccordionContent className="pt-2 pb-4 px-2">
					<div className="flex flex-col gap-2">
						{item.items.map((subItem) => (
							<motion.div key={subItem.title} whileHover={{ x: 5 }} transition={{ type: "spring", stiffness: 400, damping: 17 }}>
								<Link href={subItem.url} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#7371FF]/5 transition-all text-gray-700">
									{subItem.icon ? <span className="text-[#7371FF]">{subItem.icon}</span> : <ChevronRight className="h-3 w-3 text-gray-400" />}
									<div>
										<div className="font-medium">{subItem.title}</div>
										{subItem.description && <div className="text-xs text-gray-500 mt-0.5">{subItem.description}</div>}
									</div>
								</Link>
							</motion.div>
						))}
					</div>
				</AccordionContent>
			</AccordionItem>
		);
	}

	return (
		<motion.div whileHover={{ x: 5 }} transition={{ type: "spring", stiffness: 400, damping: 17 }}>
			<Link href={item.url} className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#7371FF]/5 transition-all text-gray-800">
				<span className="p-1.5 rounded-lg bg-[#7371FF]/10 text-[#7371FF]">{item.icon || defaultIcons[item.title]}</span>
				<span className="font-medium">{item.title}</span>
				{item.badge && <Badge text={item.badge} />}
				{item.isNew && <NewBadge />}
			</Link>
		</motion.div>
	);
});
MobileMenuItem.displayName = "MobileMenuItem";

// Logo Component
const Logo = memo(({ logo }: { logo: NavbarProps["logo"] }) => (
	<Link href={logo?.url || "/"} className="flex items-center gap-2 group">
		<motion.div whileHover={{ scale: 1.02 }} transition={{ type: "spring", stiffness: 400, damping: 10 }}>
			<Image src={logo?.src || ""} width={180} height={32} alt={logo?.alt || "Logo"} priority />
		</motion.div>
	</Link>
));
Logo.displayName = "Logo";

// Main Navbar Component
const Navbar = ({
	logo = {
		url: "https://www.everclose.com",
		src: "/assets/logo_horizontal_black.svg",
		alt: "Everclose logo",
	},
	menu = [
		{ title: "Home", url: "/", icon: <Home className="h-4 w-4" /> },
		{
			title: "Contact us",
			url: "/contact-us",
			icon: <MessageSquare className="h-4 w-4" />,
		},
	],
}: NavbarProps) => {
	const [scrolled, setScrolled] = useState(false);
	const [activeIndex, setActiveIndex] = useState<number | null>(null);

	// Header animation variants for performance
	const headerVariants = useMemo(
		() => ({
			hidden: { y: -100 },
			visible: {
				y: 0,
				transition: { type: "spring", stiffness: 300, damping: 30 },
			},
		}),
		[]
	);

	// Memoized bg class for better performance
	const headerBgClass = useMemo(() => (scrolled ? "bg-white/90 shadow-md" : "bg-transparent"), [scrolled]);

	// Scroll handler effect
	useEffect(() => {
		const handleScroll = () => {
			const isScrolled = window.scrollY > 10;
			if (isScrolled !== scrolled) {
				setScrolled(isScrolled);
			}
		};

		window.addEventListener("scroll", handleScroll, { passive: true });
		return () => window.removeEventListener("scroll", handleScroll);
	}, [scrolled]);

	return (
		<motion.header className={`fixed top-0 left-0 right-0 p-4 z-50 transition-all duration-300 backdrop-blur-md ${headerBgClass}`} initial="hidden" animate="visible" variants={headerVariants}>
			<div className="container mx-auto">
				{/* Desktop Menu */}
				<nav className="hidden justify-between lg:flex items-center">
					<motion.div className="flex items-center gap-6" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
						<Logo logo={logo} />
					</motion.div>

					<ul className="flex items-center space-x-1">
						{menu.map((item, index) => (
							<motion.li key={item.title} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: index * 0.1 }} onHoverStart={() => setActiveIndex(index)} onHoverEnd={() => setActiveIndex(null)}>
								<DesktopMenuItem item={item} isActive={activeIndex === index} />
							</motion.li>
						))}
					</ul>
					<div className="flex gap-2">
						<ActionButton />
            <LoginButton />
					</div>
				</nav>

				{/* Mobile Menu */}
				<div className="flex justify-between items-center lg:hidden">
					<Logo logo={logo} />

					<Sheet>
						<SheetTrigger asChild>
							<Button variant="outline" size="icon" className="border-[#7371FF]/20 text-[#7371FF] hover:bg-[#7371FF]/5 hover:text-[#7371FF] hover:border-[#7371FF]/30" aria-label="Open menu">
								<motion.div whileTap={{ scale: 0.9 }}>
									<Menu className="size-4" />
								</motion.div>
							</Button>
						</SheetTrigger>
						<SheetContent className="overflow-y-auto bg-white/95 backdrop-blur-md border-l border-[#7371FF]/10">
							<SheetHeader>
								<SheetTitle>
									<Logo logo={logo} />
								</SheetTitle>
							</SheetHeader>
							<div className="flex flex-col gap-6 p-2 mt-6">
								<AnimatePresence>
									<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col gap-2">
										<Accordion type="single" collapsible className="flex w-full flex-col gap-4">
											{menu.map((item, index) => (
												<motion.div key={item.title} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.1 }}>
													<MobileMenuItem item={item} />
												</motion.div>
											))}
										</Accordion>
									</motion.div>
								</AnimatePresence>

								<div className="mt-4 flex flex-col gap-2 p-2">
									<ActionButton />
                  <LoginButton />
								</div>
							</div>
						</SheetContent>
					</Sheet>
				</div>
			</div>
		</motion.header>
	);
};

export { Navbar };
